import React, { useMemo, useState } from "react";
import { Loader2, RefreshCw, Info, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { ComposedChart, Bar, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, ReferenceLine } from "recharts";
import { NAVY, GREEN, AMBER, INK, MUTE, SLOT_COLORS, num, inr, pct, monthLabel, Badge, StatusBadge, Card, btn, inp, lab } from "./common";

const tip = { contentStyle: { borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }, labelStyle: { fontWeight: 700, color: INK } };
const VIOLET = "#7C3AED";

/** One half of the sizing page — Rooftop or Open access. The caller supplies the inputs row and the result tiles;
 *  this component renders the frame, the "where each unit goes" bar, and the three views of the simulation. */
export default function SolarSection({ title, icon: Icon, accent = GREEN, sub, sim, inputs, tiles, extra, notes, testId, onRecalc, busy, canEdit, children }) {
  const [view, setView] = useState("day");
  return (
    <Card data-testid={testId} style={{ padding: 0, overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #EEF2F7", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", background: "#FBFCFE" }}>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: accent, color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}><Icon size={18} /></div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: INK }}>{title}</div>
          {sub && <div style={{ fontSize: 11.5, color: MUTE, marginTop: 1 }}>{sub}</div>}
        </div>
        {children}
      </div>

      {/* inputs */}
      <div style={{ padding: "12px 18px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", borderBottom: "1px solid #EEF2F7" }}>
        {inputs}
        {canEdit && <button onClick={onRecalc} disabled={busy} style={{ ...btn("primary"), background: accent }} data-testid={`${testId}-recalc`}>{busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}Recalculate</button>}
      </div>

      {sim ? (
        <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>{tiles}</div>
          {sim.sizing_note && <div style={{ fontSize: 11.5, color: "#92400E", display: "flex", gap: 6, alignItems: "flex-start" }}><Info size={13} style={{ flexShrink: 0, marginTop: 1 }} />{sim.sizing_note}</div>}
          {sim.capped && sim.auto_sized && <div style={{ fontSize: 11.5, color: "#92400E", display: "flex", gap: 6, alignItems: "center" }}><AlertTriangle size={13} />Held at the policy ceiling of {num(sim.cap_kw)} kW AC — the load could absorb more.</div>}
          {sim.capped && !sim.auto_sized && <div style={{ fontSize: 11.5, color: "#B91C1C", display: "flex", gap: 6, alignItems: "center" }} data-testid={`${testId}-cap-warning`}><AlertTriangle size={13} />{num(sim.kw_ac)} kW AC is above the policy ceiling of {num(sim.cap_kw)} kW AC.</div>}
          <ShareBar sim={sim} />
          {extra}
          <div>
            <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap" }}>
              {[["day", "Typical day"], ["month", "Month by month"], ...(sim.sweep ? [["sweep", "Size sweep"]] : [])].map(([k, l]) => (
                <button key={k} onClick={() => setView(k)} style={{ ...btn(view === k ? "dark" : "ghost"), padding: "5px 10px" }} data-testid={`${testId}-view-${k}`}>{l}</button>
              ))}
            </div>
            {view === "day" && <DayChart sim={sim} />}
            {view === "month" && <MonthChart sim={sim} />}
            {view === "sweep" && sim.sweep && <SweepChart sim={sim} netKey={sim.mode === "oa" ? "net_saving" : "saving"} />}
          </div>
          {notes}
        </div>
      ) : (
        <div style={{ padding: 18, fontSize: 12.5, color: MUTE }}>No simulation yet.</div>
      )}
    </Card>
  );
}

export function Tile({ label, value, sub, accent = GREEN, warn, testId }) {
  return (
    <div data-testid={testId} style={{ borderRadius: 12, border: "1px solid #E2E8F0", padding: "10px 12px", background: "#fff", borderTop: `3px solid ${warn ? "#DC2626" : accent}` }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: MUTE, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 800, color: warn ? "#DC2626" : INK, marginTop: 4, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: MUTE, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

export function Field({ label, children, width = 120 }) {
  return <div style={{ width, minWidth: 0 }}><label style={lab}>{label}</label>{children}</div>;
}
export const numInp = (value, onChange, props = {}) => <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inp} {...props} />;

const SHARES = [["realtime", "Used as generated", SLOT_COLORS.realtime], ["bess", "Via battery", VIOLET], ["banked_used", "Banked & used", SLOT_COLORS.banked], ["bank_charge", "Banking charge", "#CA8A04"], ["lapsed", "Lapsed", SLOT_COLORS.lapsed], ["export", "Net-meter credit carried", SLOT_COLORS.export]];

function ShareBar({ sim }) {
  const s = sim.shares_pct || {};
  return (
    <div data-testid="share-bar">
      <div style={{ fontSize: 11, color: MUTE, marginBottom: 5 }}>Where each generated unit ends up — {num(sim.gen)} kWh/yr at the meter{sim.losses > 0 ? ` (${num(sim.gen_busbar)} kWh at the busbar, ${pct(sim.loss_pct)} lost in wires)` : ""}</div>
      <div style={{ display: "flex", height: 14, borderRadius: 7, overflow: "hidden", background: "#F1F5F9" }}>
        {SHARES.map(([k, l, c]) => (s[k] > 0 ? <div key={k} title={`${l}: ${pct(s[k])}`} style={{ width: `${s[k]}%`, background: c }} /> : null))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 6, fontSize: 11, color: INK }}>
        {SHARES.map(([k, l, c]) => (s[k] > 0 ? <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 8, height: 8, borderRadius: 4, background: c }} />{l} <b>{pct(s[k])}</b></span> : null))}
      </div>
    </div>
  );
}

function DayChart({ sim }) {
  const rows = sim.rows || [];
  const [m, setM] = useState(() => { const i = rows.findIndex((r) => r.source === r.month); return i >= 0 ? i : Math.max(0, rows.length - 1); });
  const r = rows[Math.min(m, rows.length - 1)];
  const data = useMemo(() => (r ? r.hourly.load.map((l, h) => ({ h: `${String(h).padStart(2, "0")}`, load: l, solar: r.hourly.gen[h], used: Math.min(l, r.hourly.gen[h]), charge: r.hourly.bess_in[h], discharge: r.hourly.bess_out[h],
    grid: Math.max(0, l - Math.min(l, r.hourly.gen[h]) - r.hourly.bess_out[h]) })) : []), [r]);
  if (!r) return null;
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 11, color: MUTE, marginBottom: 4 }}>
        <span>Hour-by-hour on an operating day in</span>
        <select value={m} onChange={(e) => setM(Number(e.target.value))} style={{ ...inp, width: 150, padding: "3px 6px" }} data-testid="day-month">
          {rows.map((x, i) => <option key={x.month} value={i}>{monthLabel(x.month)}{x.source !== x.month ? ` (from ${monthLabel(x.source)} bill)` : ""}</option>)}
        </select>
        <span>· kW average per hour · ToD ₹/unit avoided: {Object.entries(r.slot_tariff || {}).map(([k, v]) => `${k} ${v}`).join(" · ")}</span>
      </div>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={data} margin={{ left: -10, right: 10, top: 5 }}>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="h" tick={{ fontSize: 10 }} interval={2} /><YAxis tick={{ fontSize: 10 }} />
          <Tooltip {...tip} formatter={(v, n) => [`${num(v)} kW`, n]} labelFormatter={(l) => `${l}:00–${String((Number(l) + 1) % 24).padStart(2, "0")}:00`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Area dataKey="solar" name="Solar at the meter" stroke={AMBER} fill="#FEF3C7" strokeWidth={2} />
          <Bar dataKey="discharge" name="Battery → load" stackId="b" fill={VIOLET} />
          <Bar dataKey="charge" name="Solar → battery" stackId="b" fill="#C4B5FD" />
          <Line dataKey="load" name="Load" stroke={NAVY} strokeWidth={2.5} dot={false} />
          <Line dataKey="grid" name="Grid draw" stroke="#94A3B8" strokeDasharray="4 3" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function MonthChart({ sim }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: MUTE, marginBottom: 4 }}>Monthly energy balance — where each solar unit ends up (kWh). Months without a bill are modelled from the nearest one.</div>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={sim.rows.map((r) => ({ ...r, m: monthLabel(r.month) + (r.source !== r.month ? "*" : "") }))} margin={{ left: -10, right: 10, top: 5 }}>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="m" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
          <Tooltip {...tip} formatter={(v, n) => [num(v), n]} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="realtime" name="Used as generated" stackId="s" fill={SLOT_COLORS.realtime} />
          <Bar dataKey="bess_in" name="Into the battery" stackId="s" fill={VIOLET} />
          <Bar dataKey="bank_used" name="Banked & used" stackId="s" fill={SLOT_COLORS.banked} />
          <Bar dataKey="bank_charge_units" name="Banking charge (in kind)" stackId="s" fill="#CA8A04" />
          <Bar dataKey="lapsed" name="Lapsed" stackId="s" fill={SLOT_COLORS.lapsed} />
          <Bar dataKey="export" name="Credit carried" stackId="s" fill={SLOT_COLORS.export} radius={[4, 4, 0, 0]} />
          <Line dataKey="load" name="Load" stroke={NAVY} strokeWidth={2} dot={false} />
          <Line dataKey="grid" name="Grid draw after solar" stroke="#94A3B8" strokeDasharray="4 3" strokeWidth={2} dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function SweepChart({ sim, netKey }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: MUTE, marginBottom: 4 }}>Size sweep — lapse rises once generation outruns what the load, the battery and the bank can absorb</div>
      <ResponsiveContainer width="100%" height={230}>
        <ComposedChart data={sim.sweep} margin={{ left: -10, right: 10, top: 5 }}>
          <CartesianGrid stroke="#F1F5F9" vertical={false} />
          <XAxis dataKey="kwp" type="number" domain={[0, "dataMax"]} tick={{ fontSize: 10 }} unit=" kWp" /><YAxis yAxisId="s" tick={{ fontSize: 10 }} tickFormatter={(v) => inr(v)} /><YAxis yAxisId="l" orientation="right" tick={{ fontSize: 10 }} unit="%" />
          <Tooltip {...tip} formatter={(v, n) => [n.includes("%") ? pct(v) : inr(v), n]} labelFormatter={(l) => `${l} kWp`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar yAxisId="s" dataKey={netKey} name={netKey === "saving" ? "Grid saving / yr" : "Net saving / yr"} fill={GREEN} radius={[4, 4, 0, 0]} barSize={18} />
          <Line yAxisId="l" dataKey="lapse_pct" name="Lapse %" stroke="#DC2626" strokeWidth={2} dot={{ r: 2 }} />
          <ReferenceLine yAxisId="l" y={2} stroke="#DC2626" strokeDasharray="3 3" />
          <ReferenceLine yAxisId="l" x={sim.kwp} stroke={AMBER} strokeWidth={2} label={{ value: `${num(sim.kwp)} kWp`, fontSize: 10, fill: "#92400E", position: "insideTopLeft" }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Note({ label, text, src, status }) {
  if (!text) return null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 11.5, color: MUTE }}>
      <span style={{ fontWeight: 800, color: INK, minWidth: 84 }}>{label}</span>
      <span style={{ flex: 1 }}>{text}{src && <span style={{ color: "#94A3B8" }}> — {src}</span>}</span>
      {status && <StatusBadge status={status} />}
    </div>
  );
}

/** Collapsible block with a one-line summary — keeps the heavy tables out of the way. */
export function Fold({ summary, children, testId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderRadius: 10, border: "1px solid #E2E8F0" }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: "#F8FAFC", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: INK, textAlign: "left", borderRadius: 10 }} data-testid={testId}>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}{summary}
      </button>
      {open && <div style={{ padding: 12 }}>{children}</div>}
    </div>
  );
}

export function BessLine({ b, isOA, state, onApply, sim }) {
  if (!b) return null;
  const m = b.mandate || {};
  const below = !!sim?.bess_below_mandate;
  return (
    <div style={{ fontSize: 11.5, color: INK, background: below ? "#FEF2F2" : "#F5F3FF", border: `1px solid ${below ? "#FCA5A5" : "#DDD6FE"}`, borderRadius: 10, padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4 }} data-testid="bess-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <b style={{ color: "#4C1D95" }}>Battery:</b>
        {m.status === "NONE" && <><span>no storage mandate in {state} — a battery is a commercial choice, never assumed.</span><StatusBadge status={m.confidence || "CONFIRMED"} /></>}
        {m.status === "DRAFT" && <><span>no storage requirement in force in {state}; a MERC <b>draft</b> would require it for new OA connectivity — not applied.</span><StatusBadge status="UNVERIFIED" /></>}
        {m.status === "IN_FORCE" && (b.required_kwh > 0
          ? <><span><b>Mandated in {state}</b> for this plant: ≥ <b>{num(b.required_kwh)} kWh / {num(b.required_kw)} kW</b> — {b.required_rule}.</span><StatusBadge status={m.confidence || "CONFIRMED"} /></>
          : <><span>{state} mandates storage only above {num(m.threshold_kw)} kW AC — this plant is below it; none required.</span><StatusBadge status={m.confidence || "CONFIRMED"} /></>)}
        {b.applied_kwh > 0 ? <span>Applied: <b>{num(b.applied_kwh)} kWh</b>{m.status === "IN_FORCE" && b.required_kwh > 0 && b.applied_kwh >= b.required_kwh ? " (meets the mandate)" : ""}.</span> : <span>None applied.</span>}
        {below && <span style={{ color: "#B91C1C", fontWeight: 800 }} data-testid="bess-below-mandate">Below the mandated minimum — the DISCOM will not connect this plant as sized.</span>}
      </div>
      <div>Economic size for this plant: {b.economic_kwh > 0 ? <b>{num(b.economic_kwh)} kWh</b> : "none needed"} <span style={{ color: MUTE }}>({b.economic_basis}; one cycle a day, {Math.round(b.rte * 100)}% round trip)</span>
        {b.with_economic && <> — would cut lapse to {pct(b.with_economic.lapse_pct)} and add <b style={{ color: GREEN }}>{inr(b.with_economic.extra_saving)}/yr</b></>}
        {b.economic_kwh > 0 && onApply && b.applied_kwh !== b.economic_kwh && <button onClick={() => onApply(b.economic_kwh)} style={{ ...btn("ghost"), padding: "2px 8px", marginLeft: 8, fontSize: 11 }} data-testid="bess-apply">Apply {num(b.economic_kwh)} kWh</button>}
      </div>
      {isOA && b.draft_min_kwh > 0 && <div>Under the MERC draft the minimum would be <b>{num(b.draft_min_kwh)} kWh / {num(b.draft_min_kw)} kW</b> <StatusBadge status={b.draft_status} />{b.with_draft_min && <> — with it the site carries <b>{num(b.with_draft_min.kwp)} kWp</b> at {pct(b.with_draft_min.lapse_pct)} lapse (<b style={{ color: GREEN }}>+{inr(b.with_draft_min.extra_saving)}/yr</b>).</>}
        <div style={{ fontSize: 10.5, color: MUTE }}>{b.draft_basis}</div></div>}
    </div>
  );
}

/** Every state rule the sizing rests on, with source and confidence. */
export function PolicyCard({ state, rows }) {
  if (!rows?.length) return null;
  return (
    <Card data-testid="policy-card">
      <div style={{ fontSize: 13, fontWeight: 800, color: INK, marginBottom: 2 }}>Policy basis — {state}</div>
      <div style={{ fontSize: 11.5, color: MUTE, marginBottom: 10 }}>Everything below is what the sizing assumes about {state}'s rules. CONFIRMED = traced to the order or regulation cited; PARTIAL = rule established, one variable open; UNVERIFIED = not found in a primary source and never priced in.</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "grid", gridTemplateColumns: "190px 1fr auto", gap: 10, alignItems: "start", fontSize: 11.5, paddingBottom: 6, borderBottom: i < rows.length - 1 ? "1px solid #F1F5F9" : "none" }}>
            <span style={{ fontWeight: 800, color: INK }}>{r.topic}</span>
            <span style={{ color: "#334155" }}>{r.text}{r.src && <span style={{ color: "#94A3B8" }}> — {r.src}</span>}</span>
            <StatusBadge status={r.status || "PARTIAL"} />
          </div>
        ))}
      </div>
    </Card>
  );
}

export function CapBadge({ sim }) {
  if (!sim?.cap_kw) return null;
  return <Badge bg={sim.capped ? "#FEF3C7" : "#F1F5F9"} fg={sim.capped ? "#92400E" : "#475569"} title="Policy ceiling on AC capacity">cap {num(sim.cap_kw)} kW AC</Badge>;
}
